export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";

/**
 * True iff the Manus OAuth portal env vars were baked into the bundle at
 * build time. When false, the Landing page should hide the "Get started"
 * button (which calls `getLoginUrl()`) and surface only the configured
 * sign-in providers (e.g. Google).
 */
export function isManusLoginConfigured(): boolean {
  const portal = import.meta.env.VITE_OAUTH_PORTAL_URL;
  const appId = import.meta.env.VITE_APP_ID;
  return Boolean(portal && appId);
}

/**
 * Build the Manus OAuth sign-in URL at call time so the redirect URI
 * tracks the current `window.location.origin` (works under both
 * `meetingmind1.duckdns.org` and an IP:port deploy).
 *
 * When the portal env vars weren't baked in, returns `"#"` so callers
 * that do `window.location.href = getLoginUrl()` or
 * `<a href={getLoginUrl()}>` don't throw / don't navigate. Conditional
 * rendering should use `isManusLoginConfigured()` to decide whether to
 * show the entry point at all.
 *
 * Why "#" and not null: every existing call site historically assumed
 * a string. Returning null here was crashing the React tree at boot
 * via `new URL("undefined/app-auth")`. "#" preserves the type signature
 * and degrades gracefully.
 */
export const getLoginUrl = (): string => {
  const oauthPortalUrl = import.meta.env.VITE_OAUTH_PORTAL_URL;
  const appId = import.meta.env.VITE_APP_ID;
  if (!oauthPortalUrl || !appId) return "#";

  const redirectUri = `${window.location.origin}/api/oauth/callback`;
  const state = btoa(redirectUri);

  let url: URL;
  try {
    url = new URL(`${oauthPortalUrl}/app-auth`);
  } catch {
    // Misconfigured portal URL — fail closed instead of crashing the SPA.
    return "#";
  }
  url.searchParams.set("appId", appId);
  url.searchParams.set("redirectUri", redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("type", "signIn");

  return url.toString();
};

/**
 * The Google sign-in entry that actually works.
 *
 * `getLoginUrl()` above is the MANUS portal helper. In production the Manus env
 * vars are unset and its callback is hard-disabled, so it returns the string
 * `"#"` — which is why both invitation pages, which used it for their signed-out
 * branch, sent invitees to a link that went nowhere. This is the same route
 * `Landing.tsx` uses for its working Google button.
 *
 * `invite` is optional and carries the invitation through the round trip. It is
 * NOT a return URL: the server accepts only a kind and a token, validates both,
 * and constructs the destination itself. Nothing here can name a destination.
 */
export const getGoogleSignInUrl = (invite?: {
  kind: "team" | "org";
  token: string;
}): string => {
  const base = "/api/auth/google/start";
  if (!invite) return base;
  return `${base}?invite=${encodeURIComponent(`${invite.kind}:${invite.token}`)}`;
};
